import { type RefObject, useLayoutEffect, useRef, useState } from "react";

import { type RowSegment, type VirtualWindow, visibleRows } from "@/lib/virtualRows";

/**
 * Janela virtual de linhas para o gerenciador (A1).
 *
 * A conta mora em `@/lib/virtualRows` (testável); aqui fica só o que precisa
 * do DOM: ler a rolagem e a altura visível do contêiner, descobrir a que
 * distância do topo a grade/lista começa, e recalcular a janela a cada
 * rolagem (uma vez por quadro) e a cada mudança de tamanho.
 *
 * `useLayoutEffect`, e não `useEffect`, para a primeira janela sair antes da
 * pintura: senão a tela mostraria o estado "sem janela" por um quadro.
 */

const EMPTY: VirtualWindow = { first: 0, last: -1, before: 0, after: 0, rows: 0 };

function sameWindow(a: VirtualWindow, b: VirtualWindow): boolean {
  return (
    a.first === b.first &&
    a.last === b.last &&
    a.before === b.before &&
    a.after === b.after &&
    a.rows === b.rows
  );
}

function segmentsKey(segments: readonly RowSegment[]): string {
  return segments.map((segment) => `${segment.count}:${segment.height}:${segment.gap}`).join("|");
}

export function useVirtualRows(options: {
  /** O elemento que rola (o `overflow-auto` da página). */
  scrollRef: RefObject<HTMLElement>;
  /** A grade ou a lista, para saber onde ela começa dentro do que rola. */
  containerRef: RefObject<HTMLElement>;
  segments: readonly RowSegment[];
  overscan?: number;
  /** Quando muda (pasta, filtro, página…), a rolagem volta ao topo antes do cálculo. */
  resetKey?: string;
}): VirtualWindow {
  const { scrollRef, containerRef, segments, overscan = 2, resetKey = "" } = options;
  const [window, setWindow] = useState<VirtualWindow>(EMPTY);
  // A chave resume os segmentos: o array costuma ser recriado a cada render,
  // e refazer a assinatura de eventos por isso seria trabalho à toa.
  const key = segmentsKey(segments);
  const segmentsRef = useRef(segments);
  segmentsRef.current = segments;
  const lastResetRef = useRef(resetKey);

  useLayoutEffect(() => {
    const scrollEl = scrollRef.current;
    const containerEl = containerRef.current;
    if (!scrollEl || !containerEl) return;
    if (lastResetRef.current !== resetKey) {
      lastResetRef.current = resetKey;
      scrollEl.scrollTop = 0;
    }

    const compute = () => {
      const scrollRect = scrollEl.getBoundingClientRect();
      const containerRect = containerEl.getBoundingClientRect();
      const offsetTop = containerRect.top - scrollRect.top + scrollEl.scrollTop;
      const next = visibleRows({
        scrollTop: scrollEl.scrollTop,
        viewportHeight: scrollEl.clientHeight,
        offsetTop,
        segments: segmentsRef.current,
        overscan,
      });
      setWindow((previous) => (sameWindow(previous, next) ? previous : next));
    };
    compute();

    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        compute();
      });
    };
    scrollEl.addEventListener("scroll", schedule, { passive: true });
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
    observer?.observe(scrollEl);
    return () => {
      scrollEl.removeEventListener("scroll", schedule);
      observer?.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [scrollRef, containerRef, key, overscan, resetKey]);

  return window;
}

export interface GridMetrics {
  columns: number;
  /** Altura de uma linha de cards (os cards são quadrados: acompanha a largura). */
  rowHeight: number;
  /** O `gap` entre linhas. */
  gap: number;
}

/**
 * Colunas, altura de linha e gap da grade, lidos do próprio layout.
 *
 * Medir, em vez de repetir os breakpoints do Tailwind aqui, é o que mantém a
 * grade virtual igual à grade de sempre: as classes continuam mandando, e a
 * conta só lê o resultado. `null` até a primeira medição (sem card não há o
 * que medir).
 */
export function useGridMetrics(gridRef: RefObject<HTMLElement>, itemCount: number): GridMetrics | null {
  const [metrics, setMetrics] = useState<GridMetrics | null>(null);

  useLayoutEffect(() => {
    const el = gridRef.current;
    if (!el || itemCount === 0) return;
    const measure = () => {
      const style = getComputedStyle(el);
      const columns = Math.max(1, style.gridTemplateColumns.split(" ").filter(Boolean).length);
      const gap = Number.parseFloat(style.rowGap) || 0;
      const first = el.firstElementChild;
      const rowHeight = first ? first.getBoundingClientRect().height : 0;
      if (rowHeight <= 0) return;
      setMetrics((previous) =>
        previous && previous.columns === columns && previous.rowHeight === rowHeight && previous.gap === gap
          ? previous
          : { columns, rowHeight, gap },
      );
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(el);
    return () => observer?.disconnect();
  }, [gridRef, itemCount]);

  return metrics;
}

export interface RowHeights {
  folder: number;
  asset: number;
}

/**
 * Altura de uma linha de pasta e de uma linha de arte na lista, medidas nos
 * primeiros exemplares desenhados (`data-kind`). Enquanto um dos tipos não
 * apareceu, usa a altura do outro — a diferença é de poucos px e a folga da
 * janela cobre.
 */
export function useRowHeights(listRef: RefObject<HTMLElement>, itemCount: number): RowHeights | null {
  const [heights, setHeights] = useState<RowHeights | null>(null);

  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el || itemCount === 0) return;
    const measure = () => {
      const folderRow = el.querySelector(':scope > [data-kind="folder"]');
      const assetRow = el.querySelector(':scope > [data-kind="asset"]');
      const folder = folderRow ? folderRow.getBoundingClientRect().height : 0;
      const asset = assetRow ? assetRow.getBoundingClientRect().height : 0;
      if (folder <= 0 && asset <= 0) return;
      const next = { folder: folder > 0 ? folder : asset, asset: asset > 0 ? asset : folder };
      setHeights((previous) =>
        previous && previous.folder === next.folder && previous.asset === next.asset ? previous : next,
      );
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(el);
    return () => observer?.disconnect();
  }, [listRef, itemCount]);

  return heights;
}
